import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { EditorState, LexicalEditor } from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import { AbbreviationPlugin } from 'modules/Lexical/Plugins/AbbreviationPlugin';
import { BasePropsPlugin } from 'modules/Lexical/Plugins/BasePropsPlugin';
import { CustomListPlugin } from 'modules/Lexical/Plugins/CustomListPlugin';
import { EditorRefPlugin } from 'modules/Lexical/Plugins/EditorRefPlugin';
import { FontBrandStylePlugin } from 'modules/Lexical/Plugins/FontBrandStylePlugin';
import { FontPlugin } from 'modules/Lexical/Plugins/FontPlugin';
import { HistoryPluginChangeHandler, HistoryWithExtraStatePlugin } from 'modules/Lexical/Plugins/HistoryWithExtraStatePlugin';
import { LinkNodePlugin } from 'modules/Lexical/Plugins/LinkNodePlugin';
import { PlaceholderPlugin } from 'modules/Lexical/Plugins/PlaceholderPlugin';
import { ReferenceCitationPlugin } from 'modules/Lexical/Plugins/ReferenceCitationPlugin';
import { SelectAllAutoFocusPlugin } from 'modules/Lexical/Plugins/SelectAllAutoFocusPlugin';
import { SSIAnchorPlugin } from 'modules/Lexical/Plugins/SSIAnchorPlugin';
import { TextBlockPlugin } from 'modules/Lexical/Plugins/TextBlockPlugin';
import { TextContentPlugin } from 'modules/Lexical/Plugins/TextContentPlugin';
import { SSIAnchorContextProvider } from 'modules/SSIAnchors/context/SSIAnchorContext';
import React, { forwardRef, RefObject, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMap } from 'typings/DeepIMap';
import TextPlaceholder from 'components/ArtboardAssets/TextPlaceholder';
import { isVeevaMedcomms, SSIPosition } from 'const';
import { DefaultTextBrandStyle } from 'const/Styles';
import { isDraggingAnchor } from 'containers/App/selectors';
import { setDocument } from 'containers/Documents/actions';
import * as Models from 'models';
import { createLexicalTextEditorEmptyState, lexicalTextEditorConfig } from '../editors';
import { LexicalOnChange } from '../hooks/useLexicalTextEditor';
import { AssetNumberPlugin } from '../Plugins/AssetNumberPlugin';
import { BrandStyleChangedPlugin } from '../Plugins/BrandStyleChangedPlugin';
import { EditModeProviderPlugin } from '../Plugins/EditModeContextPlugin';
import { EditorStateUpdatePlugin } from '../Plugins/EditorStateUpdatePlugin';
import { HeadingTagPlugin } from '../Plugins/HeadingTagPlugin';
import { NoWrapPlugin } from '../Plugins/NoWrapPlugin';
import { PastePlugin } from '../Plugins/PastePlugin';
import { ProjectMetadataPlugin } from '../Plugins/ProjectMetadataPlugin';

type Props<T> = {
  id: string;
  editorState: string | null;
  wrapperRef: RefObject<HTMLDivElement> | undefined;
  onChange: LexicalOnChange;
  editMode: boolean;
  brandProps: BrandDefinition.TextBrandProps;
  brandStyleChanged: boolean | undefined;
  brandStyles: Models.BrandStylesMap;
  historyExtraState: T;
  historyExtraStateSetter: (value: T) => void;
  historyOnChange: HistoryPluginChangeHandler;
  referencesMap: Record<string, number>;
  ssiPosition: SSIPosition | false | undefined;
  textDocument: IMap<Models.TextComponent> | undefined;
  relation: IMap<Models.LayeredRegularRelation<Models.TextRelationStyles>>;
  saveHandler: () => void;
  onBrandStyleChangedFromEditor: (changed: boolean, isFromEditor?: boolean) => void;
};

export const LexicalTextEditor = forwardRef(function <T extends object>(props: Props<T>, ref: React.Ref<LexicalEditor>) {
  const {
    id,
    editorState: maybeEmptyEditorState,
    wrapperRef,
    editMode,
    brandStyleChanged,
    brandProps,
    brandStyles,
    onChange,
    historyExtraState,
    historyExtraStateSetter,
    historyOnChange,
    referencesMap,
    ssiPosition,
    textDocument,
    relation,
    saveHandler,
    onBrandStyleChangedFromEditor,
  } = props;
  const { brandStyle, colors, fonts } = brandProps;

  const editorState = useMemo(
    () => {
      if (maybeEmptyEditorState && maybeEmptyEditorState !== 'null') {
        return maybeEmptyEditorState;
      }

      return createLexicalTextEditorEmptyState(brandStyle, colors, fonts);
    },
    // changes in brandProps and brandStyle are controlled by the plugins
    // and should not update editorState
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maybeEmptyEditorState],
  );

  const isDraggingAnchorNow = useSelector(isDraggingAnchor);

  const editorRef = useRef<LexicalEditor>(null);
  useImperativeHandle(ref, () => editorRef.current as LexicalEditor);

  const isSSIAnchorsVisible = Boolean(ssiPosition && textDocument?.get('id'));
  const isMedcomms = isVeevaMedcomms();
  const dispatch = useDispatch();

  const modifyDocument = useCallback((state: EditorState): void => {
    if (!textDocument) {
      return;
    }
    dispatch(
      setDocument(
        textDocument.set('lexicalState', JSON.stringify(state)),
      ),
    );
  }, [dispatch, textDocument]);

  return (
    <div id={id} ref={wrapperRef}>
      <SSIAnchorContextProvider
        documentId={textDocument?.get('id')}
        relationId={relation.get('id')}
        isVisible={isSSIAnchorsVisible}
      >
        <LexicalComposer initialConfig={{
          ...lexicalTextEditorConfig,
          editable: editMode,
          editorState,
        }}>
          <EditModeProviderPlugin editMode={editMode}>
            <div className="editor-container">
              <EditorStateUpdatePlugin editorState={editorState} />
              <RichTextPlugin
                contentEditable={<ContentEditable className="editor-input" data-testid="editor-input" />}
                ErrorBoundary={LexicalErrorBoundary}
              />
              {editMode && <HistoryWithExtraStatePlugin
                extraState={historyExtraState}
                extraStateSetter={historyExtraStateSetter}
                mergeExtraStateKeys={['brandStyleChanged']}
                onChange={historyOnChange}
              />}
              <BrandStyleChangedPlugin
                brandStyle={brandStyle ?? brandStyles.get(DefaultTextBrandStyle)}
                colors={colors}
                fonts={fonts}
                onBrandStyleChanged={onBrandStyleChangedFromEditor}
              />
              <ListPlugin />
              <LinkPlugin />
              <EditorRefPlugin ref={editorRef} />
              <TabIndentationPlugin maxIndent={5} />
              <BasePropsPlugin onChange={onChange.baseProps} />
              <TextBlockPlugin
                brandStyle={brandStyle ?? brandStyles.get(DefaultTextBrandStyle)}
                brandStyleChanged={brandStyleChanged}
                editorState={editorState}
                onChange={onChange.textBlock}
              />
              <CustomListPlugin
                onChange={onChange.bulletToggle}
                colors={colors}
                editorState={editorState}
                brandStyle={brandStyle}
                brandStyleChanged={brandStyleChanged}
              />
              <FontPlugin
                defaultBrandColor={brandStyle ? BrandDefinition.getDefaultBrandColor(brandStyle, colors) : undefined}
                onChange={onChange.font}
                colors={colors}
                fonts={fonts}
              />
              <FontBrandStylePlugin
                brandStyle={brandStyle}
                brandStyleChanged={brandStyleChanged}
                colors={colors}
                fonts={fonts}
              />
              <AbbreviationPlugin onChange={onChange.abbreviations} />
              <LinkNodePlugin onChange={onChange.link} />
              <TextContentPlugin onChange={onChange.textContent} />
              <ReferenceCitationPlugin
                referencesMap={referencesMap}
                saveHandler={saveHandler}
              />
              <SelectAllAutoFocusPlugin />
              <PlaceholderPlugin placeholder={<TextPlaceholder />} />
              {isSSIAnchorsVisible && <SSIAnchorPlugin
                showPlaceholder={isDraggingAnchorNow}
                modifyDocument={modifyDocument}
              />}
              <PastePlugin />
              <NoWrapPlugin onChange={onChange.textNoWrap} />
              <ProjectMetadataPlugin />
              {isMedcomms && <AssetNumberPlugin modifyDocument={modifyDocument} />}
              <HeadingTagPlugin
                onChange={onChange.headingTag}
                brandStyle={brandStyle}
                brandStyleChanged={brandStyleChanged}
              />
            </div>
          </EditModeProviderPlugin>
        </LexicalComposer>
      </SSIAnchorContextProvider>
    </div>
  );
});
